"use client";

import { Core } from "@faire/design-tokens";
import { tsv } from "@faire/slate/lib/tsv";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { extractImageCropFromTag } from "@faire/web/common/images/cropUtils/extractImageCropFromTag";
import {
  optimizeImageUrl,
  optimizeImageUrlFillSquare,
} from "@faire/web/common/images/optimizeImages";
import { useStrictLocalization } from "@faire/web/common/localization";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { cn } from "@faire/web/tools/cn";
import { useUniqueId } from "@faire/web/ui/hooks/useUniqueId";
import * as React from "react";

import { Swatch as SwatchProps } from "./types";

const INNER_DIAMETER_LARGE = 40;

/**
 * @trackcomponent
 */
export const Swatch = ({
  "aria-label": ariaLabel,
  "data-test-id": dataTestId,
  color,
  image,
  isAvailable = true,
  isChecked = false,
  isDisabled = false,
  name,
  onChange,
  isSmallSize = false,
  ...rest
}: SwatchProps.IProps) => {
  const { strictLocalize } = useStrictLocalization();
  const labelId = useUniqueId("swatch-label-");
  const [imageError, setImageError] = React.useState(false);
  const isSSR = useIsSSR();

  const onImageError = () => {
    if (!imageError) {
      setImageError(true);
    }
  };

  const unavailable: string = strictLocalize({
    id: "Ec6S71",
    defaultMessage: "Unavailable",
    description: {
      text: "alt text for screen reader, to indicate the swatch option is unavailable",
    },
  });

  const accessibleLabel = isAvailable
    ? ariaLabel
    : `${unavailable} ${ariaLabel}`;

  const { innerSize, outerSize } = getSwatchSizeClasses(isSmallSize);
  const labelCls = labelStyles.cls({
    className: outerSize,
    isChecked,
    isDisabled,
  });

  return (
    <div>
      <input
        aria-label={accessibleLabel}
        checked={isChecked}
        data-test-id={dataTestId}
        disabled={isDisabled}
        id={labelId}
        name={name}
        onChange={onChange}
        readOnly={onChange === undefined}
        type="radio"
        className="peer absolute h-px w-px overflow-hidden border-0 p-0"
        style={{
          clip: "rect(1px, 1px, 1px, 1px)",
          clipPath: "inset(0 0 99.9% 99.9%)",
        }}
        {...rest}
      />
      <label
        htmlFor={labelId}
        className={labelCls}
        style={{
          // outline: auto is not present in tailwind
          outlineStyle: "auto !important",
          outlineWidth: "0px",
        }}
      >
        {image && !imageError ? (
          <img
            alt="" // This empty alt is on purpose, aria-label provides the value
            onError={onImageError}
            src={optimizeSwatchImage(image, isSSR)}
            className={cn("rounded-full", innerSize)}
          />
        ) : (
          <div
            className={cn("rounded-full", innerSize)}
            style={{
              backgroundColor: color || Core.surface.subdued,
            }}
          />
        )}
        {isAvailable ? null : (
          <span className="bg-core-border-default border-core-surface-inverse absolute block h-[4px] w-[52px] rotate-[-45deg] border" />
        )}
      </label>
    </div>
  );
};

export const optimizeSwatchImage = (image: Partial<IImage>, isSSR: boolean) => {
  if (extractImageCropFromTag(image.tags)) {
    return optimizeImageUrl(image, isSSR, {
      width: INNER_DIAMETER_LARGE,
      height: INNER_DIAMETER_LARGE,
    });
  }
  return optimizeImageUrlFillSquare(image, isSSR, INNER_DIAMETER_LARGE);
};

const getSwatchSizeClasses = (isSmallSize: boolean) => {
  if (isSmallSize) {
    return {
      innerSize: "h-5 w-5 lg:h-4 lg:w-4",
      outerSize: "h-6 w-6 lg:h-5 lg:w-5",
    };
  }
  return {
    innerSize: "h-10 w-10",
    outerSize: "h-12 w-12",
  };
};

const labelStyles = tsv({
  base: [
    "relative box-border flex items-center justify-center rounded-full border",
    "peer-focus-visible:border-core-border-subdued outline-offset-3 peer-focus-visible:outline-5! peer-focus-visible:outline-[-webkit-focus-ring-color]! peer-focus-visible:outline-[Highlight]",
  ],
  variants: {
    isChecked: {
      true: "border-core-border-enabled",
      false: "border-transparent",
    },
    isDisabled: {
      true: "cursor-default",
      false:
        "hover:border-core-border-subdued active:border-core-border-enabled cursor-pointer",
    },
  },
});
